import React from 'react';
import { Link } from 'react-router-dom';
import { Video, Menu, X } from 'lucide-react';

interface NavbarProps {
  onLogoClick?: () => void;
}

function Navbar({ onLogoClick }: NavbarProps) {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [isMenuOpen]);

  const handleLogoClick = () => {
    setIsMenuOpen(false);
    if (onLogoClick) onLogoClick();
  };

  return (
    <nav 
      className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-slate-900/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
      }`}
      aria-label="Main navigation"
    >
      <div className="container mx-auto px-6 py-6">
      <div className="flex justify-between items-center">
        <Link 
          to="/"
          className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
          onClick={handleLogoClick}
          aria-label="Techton Video Meetings - Go to Homepage"
        >
          <Video className="w-8 h-8 text-blue-400" aria-hidden="true" /> 
          <span className="text-2xl font-bold">Techton</span>
        </Link>

        {/* Mobile menu button */}
        <button
          className="md:hidden text-gray-400 hover:text-white transition"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMenuOpen}
        >
          {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-8">
          <Link 
            to="/"
            className="bg-blue-700 hover:bg-blue-800 px-6 py-2 rounded-full font-medium transition"
            aria-label="Join Meeting"
            onClick={onLogoClick ? () => {} : undefined}
          >
            Join Meeting
          </Link>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 z-50 bg-slate-900/95 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex flex-col items-center justify-center min-h-screen space-y-8">
              <Link
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className="bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-full font-medium transition text-xl"
              >
                Join Meeting
              </Link>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="absolute top-6 right-6 text-gray-400 hover:text-white"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
    </nav>
  );
}

export { Navbar };